// Eskom PARCC — filter-friendly working-paper export (9 Jun call; finding rows
// added Jun 11 2026 call).
//
// Emits ONE ROW PER CES CRITERION, repeating the transaction + grey-header context
// on every row, so the workbook is genuinely filterable: AutoFilter (applied by
// the workbook glue) drills to a transaction, a procedure or a result, and any
// column can be hidden without losing the rest.
//
// Jun 11 2026: Lindiwe reported a per-transaction download showed "missing" fields
// — the working-paper-level Finding Text cell squashed several findings into one
// cell that Excel hid without wrap (SheetJS CE writes no cell styles, so wrap text
// is not an option). The fix is structural: when the findings join is supplied,
// each finding is emitted on its OWN ROW with dedicated columns (Finding # /
// Detail / Compliance / Status), so every finding is on its own line and visible.
// A transaction with neither criteria nor findings still emits one context row, so
// the population is always complete.
import type { Transaction } from '../../data/types';
import type { WorkingPaperRead, CesRowRead, FindingRead } from '../../lib/db';
import { formatRand } from '../../lib/money';
import { neutraliseCell } from './neutraliseCell';
import { genuineFindings } from '../../domain/findings';

/** Map joins, all keyed by `referenceNo`. */
export type WorkingPapersByRef = ReadonlyMap<string, WorkingPaperRead>;
export type CesRowsByRef = ReadonlyMap<string, readonly CesRowRead[]>;
export type FindingsByRef = ReadonlyMap<string, readonly FindingRead[]>;

/** The filterable column set: atomic context columns, the per-criterion trio, then
 *  the per-finding columns (Jun 11 2026 — one finding per row). The working-paper
 *  summary columns (Reviewer Email / Reviewed At / Working Paper Notes / Finding
 *  Text) stay (Lindiwe regression, restored 7 Jun). */
export const WORKING_PAPER_CRITERIA_HEADERS = [
  'Reference No',
  'Dataset',
  'Division',
  'BU',
  'Brief Description',
  'Supplier',
  'Contract/PO Number',
  'Value of Contract/PO',
  'Buyer',
  'PR Number',
  'Procurement Manager',
  'End-User',
  'CSD Number',
  'Mechanism',
  'Assigned To',
  'Reviewer Email',
  'Review Status',
  'Compliance Level',
  'Reviewed At',
  'Working Paper Notes',
  'Finding Text',
  // Per-CES-criterion checklist columns.
  'Stage',
  'No',
  'Procedure',
  'Result',
  'Finding',
  // Per-finding columns (one finding per row) — blank on criterion rows.
  'Finding #',
  'Finding Detail',
  'Finding Compliance',
  'Finding Status',
] as const;

// Trailing-column widths: 5 criterion columns + 4 finding columns.
const BLANK_CRITERION = ['', '', '', '', ''];
const BLANK_FINDING = ['', '', '', ''];

/**
 * The repeated transaction/working-paper context cells (everything before Stage).
 * The captured review fields fall back from the working paper to the transaction,
 * exactly as the old flat sheet did (the monolith captured these on the
 * transaction; the SQL-native model moves them to the working paper).
 */
function contextCells(tx: Transaction, wp: WorkingPaperRead | undefined): string[] {
  return [
    tx.referenceNo ?? '',
    tx.dataset ?? '',
    tx.division ?? '',
    tx.bu ?? tx.ou ?? '',
    tx.briefDescription ?? '',
    tx.supplier ?? '',
    tx.contractNumber ?? '',
    tx.value ? formatRand(tx.value) : (tx.valueDisplay ?? ''),
    tx.buyer ?? '',
    tx.prNumber ?? '',
    tx.procurementManager ?? '',
    tx.endUser ?? '',
    tx.csdNumber ?? '',
    tx.mechanism ?? '',
    tx.assignedToEmail ?? '',
    wp?.reviewerEmail ?? tx.reviewerEmail ?? '',
    (wp?.reviewStatus ?? tx.reviewStatus ?? '') as string,
    (wp?.complianceLevel ?? tx.complianceLevel ?? '') as string,
    wp?.reviewedAt ?? tx.reviewedAt ?? '',
    wp?.workingPaperNotes ?? tx.workingPaperNotes ?? '',
    wp?.findingText ?? tx.findingText ?? '',
  ];
}

/**
 * Build the filterable working-paper sheet: header, then per transaction one row
 * per CES criterion AND (when `findingsByRef` is supplied) one row per finding —
 * so each finding is on its own line. A transaction with neither emits one context
 * row, so the population is always complete. Pass the transactions to export
 * (already scoped/filtered by the caller) plus the working-paper, CES-row and
 * (optional) finding joins. Pure — no XLSX, no DOM; every cell neutralised.
 */
export function buildWorkingPaperCriteriaSheet(
  transactions: readonly Transaction[],
  workingPapersByRef: WorkingPapersByRef,
  cesRowsByRef: CesRowsByRef,
  findingsByRef: FindingsByRef = new Map(),
): string[][] {
  const out: string[][] = [[...WORKING_PAPER_CRITERIA_HEADERS]];
  for (const tx of transactions) {
    const wp = workingPapersByRef.get(tx.referenceNo);
    const context = contextCells(tx, wp);
    const criteria = cesRowsByRef.get(tx.referenceNo) ?? [];
    // Genuine findings only — one per criterion, no "Additional comments" notes
    // (owner feedback Aug 2026: raw rows inflated the per-transaction count).
    const findings = genuineFindings(findingsByRef.get(tx.referenceNo) ?? []);

    // One row per CES criterion (checklist columns filled, finding columns blank).
    for (const c of criteria) {
      out.push(
        [
          ...context,
          c.stage ?? '',
          String(c.seq ?? ''),
          c.criterion ?? '',
          c.status ?? '',
          c.finding ?? '',
          ...BLANK_FINDING,
        ].map(neutraliseCell),
      );
    }

    // One row per finding (finding columns filled, checklist columns blank) — each
    // finding on its own line. Compliance/status fall back through the captured
    // fields so the column is never blank for a real finding.
    findings.forEach((f, i) => {
      out.push(
        [
          ...context,
          ...BLANK_CRITERION,
          String(i + 1),
          f.text ?? '',
          (f.finalCompliance ?? f.suggestedCompliance ?? '') as string,
          f.effectiveStatus ?? f.legacyStatus ?? '',
        ].map(neutraliseCell),
      );
    });

    // Neither criteria nor findings — still emit one context row for completeness.
    if (criteria.length === 0 && findings.length === 0) {
      out.push([...context, ...BLANK_CRITERION, ...BLANK_FINDING].map(neutraliseCell));
    }
  }
  return out;
}
